import type { Session } from '@supabase/supabase-js';
import { getSupabaseClient } from './supabase';

/** OAuth code captured before React/AuthProvider init (see bootstrapOAuthCallbackRoute). */
let pendingCallbackCode: string | null = null;

/** Module-level dedupe survives React StrictMode remounts and HMR in dev. */
let inflightExchange: Promise<Session | null> | null = null;

/** Single-flight callback resolution shared across StrictMode effect re-runs. */
let callbackResolution: Promise<Session | null> | null = null;

export function isAuthCallbackRoute(pathname: string = window.location.pathname): boolean {
  return pathname === '/auth/callback' || pathname.startsWith('/auth/callback/');
}

/**
 * Call from `main.tsx` before React mounts.
 *
 * Consumes `?code=` immediately so AuthProvider's `getSession()` cannot run
 * `_recoverAndRefresh()` → `_removeSession()` and delete the PKCE verifier first.
 */
export function bootstrapOAuthCallbackRoute(): void {
  if (typeof window === 'undefined' || !isAuthCallbackRoute()) {
    return;
  }
  pendingCallbackCode = consumeOAuthCallbackCode();
}

/**
 * Read OAuth `code` from the callback URL once, then strip it so a second
 * StrictMode effect pass cannot call `exchangeCodeForSession` again.
 */
export function consumeOAuthCallbackCode(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (!code) {
    return null;
  }
  params.delete('code');
  const remainder = params.toString();
  const nextPath = remainder
    ? `${window.location.pathname}?${remainder}`
    : window.location.pathname;
  window.history.replaceState({}, '', nextPath);
  return code;
}

function takeOAuthCallbackCode(): string | null {
  const code = pendingCallbackCode;
  pendingCallbackCode = null;
  return code ?? consumeOAuthCallbackCode();
}

/**
 * Exchange the OAuth code for a session. Concurrent callers share one promise
 * so PKCE verifier is consumed exactly once.
 */
export function exchangeOAuthCodeForSession(code: string): Promise<Session | null> {
  if (!inflightExchange) {
    inflightExchange = (async (): Promise<Session | null> => {
      const supabase = getSupabaseClient();
      if (!supabase) {
        return null;
      }
      const { data, error } = await supabase.auth.exchangeCodeForSession(code);
      if (error) {
        throw error;
      }
      return data.session;
    })().finally(() => {
      inflightExchange = null;
    });
  }
  return inflightExchange;
}

async function sessionAfterExchangeFailure(): Promise<Session | null> {
  const supabase = getSupabaseClient();
  if (!supabase) {
    return null;
  }
  const { data } = await supabase.auth.getSession();
  return data.session;
}

async function resolveOAuthCallbackSessionInternal(): Promise<Session | null> {
  const supabase = getSupabaseClient();
  if (!supabase) {
    return null;
  }

  const code = takeOAuthCallbackCode();
  if (code) {
    try {
      return await exchangeOAuthCodeForSession(code);
    } catch (error: unknown) {
      // StrictMode or a stale tab may have already exchanged this code.
      const existing = await sessionAfterExchangeFailure();
      if (existing) {
        return existing;
      }
      throw error;
    }
  }

  if (inflightExchange) {
    try {
      const session = await inflightExchange;
      if (session) {
        return session;
      }
    } catch {
      const existing = await sessionAfterExchangeFailure();
      if (existing) {
        return existing;
      }
    }
  }

  const { data, error } = await supabase.auth.getSession();
  if (error) {
    throw error;
  }
  return data.session;
}

/**
 * Resolve the OAuth callback session exactly once per page load.
 *
 * StrictMode runs the callback effect twice: the first pass consumes `?code=`
 * and starts PKCE exchange; the second pass must await that in-flight work
 * instead of concluding "no session" while exchange is still running.
 */
export function resolveOAuthCallbackSession(): Promise<Session | null> {
  if (!callbackResolution) {
    callbackResolution = resolveOAuthCallbackSessionInternal();
  }
  return callbackResolution;
}

/** Test-only reset for unit tests. */
export function resetOAuthExchangeStateForTests(): void {
  pendingCallbackCode = null;
  inflightExchange = null;
  callbackResolution = null;
}
